import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Layers, Shield, Ruler, Wrench, CheckCircle2 } from "lucide-react";
import heroImage from "@/assets/stone-masonry-installation-oakland-nj.webp";
import stoneMasonryTotowa from "@/assets/stone-masonry-totowa-passaic-nj.webp";
import stoneMasonryStepsVerona from "@/assets/stone-masonry-steps-verona-essex-county-nj.webp";
import stoneVeneerStepsWayne from "@/assets/stone-veneer-steps-installation-wayne-nj.webp";
import stoneWorkTotowa from "@/assets/stone-work-totowa-nj.webp";
import stoneVeneerParamus from "@/assets/stone-veneer-installation-paramus-bergen-county-nj.webp";


const CitationLink = ({ num, url, children }: { num: number; url: string; children: React.ReactNode }) => (
  <>
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">{children}</a>
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline"><sup>[{num}]</sup></a>
  </>
);

const contentSections: Array<{
  icon: React.ElementType;
  title: string;
  image: string;
  imageAlt: string;
  objectFit?: string;
  aspectRatio?: string;
  content: string[];
  citation: { num: number; url: string; text: string };
}> = [
  {
    icon: Layers,
    title: "Stone Masonry Starts With the Right Base and Materials",
    image: stoneMasonryTotowa,
    imageAlt: "Variety of stone masonry patterns, including stacked stone, brick, and river rock in Totowa, Passaic County, NJ.",
    content: [
      "A proper footing and base can prevent settling, cracking, and water damage before any of it has a chance to start. Because North Jersey goes through so many freeze-thaw cycles each year, footings need to sit below the local frost line, which is typically around 36 inches in Bergen and Passaic Counties.",
      "Clay-heavy soils across this region retain moisture and expand when wet, which can create problems if the base is not laid properly to handle it. A compacted gravel base with proper drainage is what keeps stone walls stable over time, because without that drainage path, water pressure can build beneath the footing and cause the whole thing to shift.",
      "There are five main types of stone masonry: rubble, ashlar, dry stone, veneer, and slip form. For residential projects in North Jersey, rubble and ashlar are the most common. Veneer is popular when someone wants to upgrade a facade without adding too much weight to the structure. And dry stone works well for garden borders and decorative walls that don't need to carry any structural load."
    ],
    citation: { num: 1, url: "https://www.nj.gov/dca/divisions/codes", text: "NJ Uniform Construction Code" }
  },
  {
    icon: Shield,
    title: "Stone Veneer and Full Stone Serve Different Purposes",
    image: stoneMasonryStepsVerona,
    imageAlt: "Newly installed custom stone masonry steps leading to a home's front porch in Verona, Essex County, NJ.",
    content: [
      "Homeowners in Montclair and Morristown often ask whether they need veneer or full-depth stone for their project. The answer really depends on what the wall is going to do.",
      "Stone veneer is thinner and lighter, so it bonds to an existing surface and works well for facade upgrades on Colonials and Tudors. A lot of older foundations in North Jersey can not handle the additional load that comes with full-depth stone, and veneer solves that problem. One detail that tends to get overlooked is the gap above grade. If veneer contacts the ground, moisture can wick up into the material, which leads to damage over time.",
      "Full stone is structural and load-bearing. Retaining walls, freestanding garden walls, and new construction will typically call for full-depth stone, especially when the wall has to support weight or hold back soil."
    ],
    citation: { num: 3, url: "https://www.astm.org", text: "ASTM C270" }
  },
  {
    icon: Ruler,
    title: "Site Prep and Structural Support Keep Stone Walls Sound",
    image: stoneVeneerStepsWayne,
    imageAlt: "Newly installed stone veneer steps leading to a home with stone siding in Wayne, Passaic County, NJ, by Masonry Pro.",
    content: [
      "North Jersey's hilly terrain in towns like Ridgewood and Wayne often means retaining walls are part of the job, and those walls need proper drainage behind the structure to perform the way they should. Getting the excavation, grading, and reinforcement right from the start is what prevents wall failure and costly rebuilds later on.",
      "How deep we excavate depends on the wall height and the soil type. Taller walls on softer soils will need deeper footings. Gravel backfill and weep holes are what manage the water pressure behind retaining walls, because they give moisture a way to escape instead of building up against the back of the wall.",
      "Rebar or wall ties add strength, and this is especially true for walls over four feet. Without some form of reinforcement, soil pressure and seasonal ground movement can push a wall forward over time, and that kind of damage is expensive to fix once it starts."
    ],
    citation: { num: 4, url: "https://www.masonrysociety.org", text: "TMS 402/602" }
  },
  {
    icon: Wrench,
    title: "What Goes Into a Stone Masonry Installation",
    image: stoneWorkTotowa,
    imageAlt: "Masonry contractor performing stone work on a residential exterior in Totowa, Passaic County, NJ.",
    content: [
      "Knowing what the process looks like can help you set realistic timelines and recognize quality workmanship when you see it. Everything starts with the footing. If the footing is not right, the wall above it will not hold up the way it should.",
      "Mortar joint tooling is one detail that separates professional work from the rest. When joints are tooled properly, they resist water penetration and hold up better through North Jersey's freeze-thaw cycles. If joints are left rough or unfinished, they tend to absorb moisture and can crack within a few seasons.",
      "Dry stone construction is an alternative that uses no mortar at all. Each stone gets fitted against the surrounding pieces by hand. It works best for garden borders and decorative walls where the structural demand is limited.",
      "Permit requirements will vary by municipality. A lot of towns in Essex and Morris Counties require structural permits for walls above a certain height. We check the local requirements and handle the permit process as part of the project."
    ],
    citation: { num: 5, url: "https://www.gobrick.com", text: "BIA Technical Note 7A" }
  },
  {
    icon: CheckCircle2,
    title: "Rebar, Expansion Joints, and Drainage Protect Long-Term Results",
    image: stoneVeneerParamus,
    imageAlt: "Stone veneer column installed on a residential garage in Paramus, Bergen County, NJ, by Masonry Pro Construction.",
    content: [
      "Reinforcement, joint placement, and drainage are the three factors that matter most for long-term wall performance. Rebar is required in most load-bearing and retaining walls, and it is one of those details that you can not skip if you want the wall to last.",
      "North Jersey temperatures can swing from single digits in January to over 90 in July. That range puts stress on stone and mortar year after year, and it adds up. Expansion joints placed at regular intervals help absorb that movement. Without them, cracks will develop, and water will get in, which is when the real damage starts.",
      "Drainage behind retaining walls is just as important. Gravel backfill and weep holes give water somewhere to go instead of building up behind the structure. Homeowners in Maplewood and Glen Rock who have had walls repaired often find out that drainage was missing from the original installation, and that was the root cause of the failure."
    ],
    citation: { num: 4, url: "https://www.masonrysociety.org", text: "TMS 402/602" }
  },
];

const faqs = [
  {
    question: "How long does a stone masonry installation take in North Jersey?",
    answer: "Most residential stone wall projects take somewhere between 1 and 3 weeks, depending on the wall size, the type of stone, and the site conditions. Retaining walls that need drainage and rebar will take longer than a veneer facade upgrade. Weather can also shift the timeline, especially if we are working around spring rain in Bergen or Passaic County."
  },
  {
    question: "What is the difference between stone veneer and full stone?",
    answer: "Stone veneer is a thinner and lighter material that bonds to an existing surface, so it works well for facade upgrades on older homes where the foundation can not handle extra weight. Full stone is structural and load-bearing, making it the right choice for retaining walls, garden walls, and new construction that must support soil or structural loads."
  },
  {
    question: "Do I need a permit for a stone wall in North Jersey?",
    answer: "It depends on the municipality and how tall the wall is. Many towns in Essex and Morris Counties require structural permits for walls over a certain height. We check the local requirements for your specific town and handle the permit process as part of the project, so you don't have to manage that yourself."
  },
  {
    question: "What type of stone works best for residential masonry in North Jersey?",
    answer: "Rubble and ashlar stone are the most common for residential projects in this area. Bluestone, fieldstone, and limestone are all popular choices, and the right one depends on whether the wall is structural, decorative, or serving as a veneer. We can walk you through the options during a site visit and help you determine what makes sense for your property and budget."
  },
  {
    question: "How deep do footings need to be for stone walls in North Jersey?",
    answer: "Footings have to sit below the local frost line so the wall doesn't heave or crack when the ground freezes. In Bergen and Passaic Counties, the frost line is typically around 36 inches. If the soil is clay-heavy, which it often is in this region, additional depth or drainage may be needed to keep the footing stable through the seasonal changes."
  },
  {
    question: "How often should stone masonry be maintained?",
    answer: "It is a good idea to check mortar joints and wall caps at least once a year, ideally in the spring after winter has passed. Repointing mortar joints every 15 to 25 years is normal for most residential walls. Ice pushes into small cracks over the winter and widens them, so catching damage early and repointing before water gets behind the stone can prevent bigger repairs later."
  },
];

const citations = [
  {
    num: 1,
    text: "New Jersey Uniform Construction Code (N.J.A.C. 5:23) - Permit and structural requirements for masonry construction in New Jersey.",
    url: "https://www.nj.gov/dca/divisions/codes"
  },
  {
    num: 2,
    text: "Portland Cement Association - Masonry Information - Mortar curing requirements and seasonal installation guidance.",
    url: "https://www.cement.org"
  },
  {
    num: 3,
    text: "ASTM C270 - Standard Specification for Mortar for Unit Masonry - Mortar type specifications and application standards.",
    url: "https://www.astm.org"
  },
  {
    num: 4,
    text: "TMS 402/602 - Building Code Requirements and Specification for Masonry Structures - Structural design standards for load-bearing and retaining walls.",
    url: "https://www.masonrysociety.org"
  },
  {
    num: 5,
    text: "Brick Industry Association - Technical Note 7A: Water Resistance of Brick Masonry - Joint tooling techniques and water resistance in masonry construction.",
    url: "https://www.gobrick.com"
  },
];
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

export default function StoneMasonryInstallation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Stone Masonry Installation Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="Is your stone wall project on the list this year? We handle stone masonry installation across Passaic, Bergen, Essex, and Morris counties. Free quote." />
        <meta property="og:description" content="Is your stone wall project on the list this year? We handle stone masonry installation across Passaic, Bergen, Essex, and Morris counties. Free quote." />
        <meta property="og:title" content="Stone Masonry Installation Totowa, NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Stone Masonry Installation",
            description: "Professional stone masonry installation across Passaic, Bergen, Essex, and Morris counties.",
            url: "/masonry-services/stone-masonry",
            image: "https://masonry-pro.com/og/stone-veneer.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/stone-veneer.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/stone-veneer.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Natural stone masonry installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Stone Masonry Installation in Totowa, NJ Serving <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Stone masonry can add real, lasting value to homes and commercial properties in North Jersey, and that holds true across every season. This page covers stone wall construction, stone veneer installation, retaining walls, and facade work for both residential and commercial buildings. We are available year-round, with scheduling based on the scope of the project.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="#contact">Get a Free Quote</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Content Sections */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="space-y-16 max-w-5xl mx-auto">
                {contentSections.map((section, index) => (
                  <div key={section.title} className="grid md:grid-cols-2 gap-8 items-start">
                    {/* Text Content - alternating sides */}
                    <div className={index % 2 === 1 ? "md:order-2" : ""}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                          <section.icon className="h-6 w-6 text-secondary" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold">{section.title}</h2>
                      </div>
                      <div className="space-y-4 text-muted-foreground">
                        {section.content.map((paragraph, pIndex) => (
                          <p key={pIndex}>
                            {pIndex === 0 && section.citation ? (
                              <>
                                {paragraph.split('.')[0]}.
                                <CitationLink num={section.citation.num} url={section.citation.url}>
                                  {section.citation.text}
                                </CitationLink>
                                {paragraph.includes('.') ? paragraph.substring(paragraph.indexOf('.') + 1) : ''}
                              </>
                            ) : (
                              paragraph
                            )}
                          {index === 0 && pIndex === 0 && (<> Stone is where a <Link to="/" className="text-secondary hover:underline">local masonry contractor</Link> with real field experience makes the biggest difference.</>)}
                          </p>
                        ))}
                      </div>
                    </div>
                    
                    {/* Image */}
                    <div className={index % 2 === 1 ? "md:order-1" : ""}>
                      <div className={`${section.aspectRatio ?? "aspect-[4/3]"} rounded-xl overflow-hidden border border-border`}>
                        <img
                          src={section.image}
                          alt={section.imageAlt}
                          className={`w-full h-full ${section.objectFit ?? "object-cover"}`}
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Snippet */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="bg-background rounded-xl p-8 border border-border">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    How Is Stone Masonry Installation Done in North Jersey?
                  </h2>
                  <ol className="space-y-3 text-muted-foreground">
                    <li className="flex gap-3">
                      <span className="font-bold text-secondary">1.</span>
                      Assess the site, check the soil conditions, and confirm footing depth based on the local frost line requirements.
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-secondary">2.</span>
                      Excavate and pour a reinforced concrete footing below grade.
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-secondary">3.</span>
                      Lay a compacted gravel drainage base around and behind the footing so water has a path to drain.
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-secondary">4.</span>
                      Set the first course of stone with mortar, and check level and alignment as you go.
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-secondary">5.</span>
                      Build additional courses, plumbing and leveling each one. Install wall ties or rebar as needed to meet height or load requirements.
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-secondary">6.</span>
                      Tool the mortar joints to shed water and resist freeze-thaw damage.<sup className="text-secondary">[5]</sup>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-secondary">7.</span>
                      Allow the mortar to cure fully, then seal and inspect the finished wall.
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection 
            title="See Our Stone Masonry"
            highlightedTitle="Work in Action"
            videoIds={["0Kid2ea7tik", "BiS2ELaDD0E", "G_i-bncb70U"]}
          />

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" hubOverride={{ href: "/", label: "Masonry Contractor in North Jersey" }} />

          {/* CTA Section */}
          <CTASection 
            serviceName="Stone Masonry"
            heading="Ready for Your"
            highlightedHeading="Stone Masonry Project?"
          />

          {/* Citations */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      References & Citations
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground">
                        {citations.map((citation) => (
                          <li key={citation.num} className="flex gap-2">
                            <span className="text-secondary font-bold">[{citation.num}]</span>
                            <span>
                              {citation.text}{" "}
                              <a 
                                href={citation.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-secondary hover:underline"
                              >
                                {citation.url}
                              </a>
                            </span>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
